import React, { useState } from "react"
import {
  Card,
  CardBody,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
  CardHeader,
  CardFooter,
  Col,
  Alert,
  Badge
} from "reactstrap"
import Select from "react-select"
import Datetime from "react-datetime"
import moment from "moment"
import { FilePond, registerPlugin } from "react-filepond"
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation"
import FilePondPluginImagePreview from "filepond-plugin-image-preview"
import { useMutation, useQuery } from "@apollo/react-hooks"
import { withRouter } from "react-router"
import { flowRight as compose } from "lodash"
import { withBasicNotify } from "components/notification"
import { BackButton } from "components/utility"
import { BarLoader, Spinner } from "components/spinner"
import { Img } from "components/image"
import { getCombinedOption, getAssociatedOptions, getOptions } from "utils"
import { maintainServiceStatusTypes, findTypeValueByKey } from "utils/types"
import {
  CREATE_MAINTAIN_SERVICE,
  UPDATE_MAINTAIN_SERVICE,
  GET_COMPANIES
} from "../gql"

registerPlugin(FilePondPluginImageExifOrientation, FilePondPluginImagePreview)

const MaintainServiceForm = props => {
  const [localData, setLocalData] = useState(
    props.data || {
      description: "",
      response: "",
      company: "",
      status: "",
      user: "",
      photoUrls: [],
      files: []
    }
  )
  const [
    updateObject,
    { loading: updateLoading, error: updateError }
  ] = useMutation(UPDATE_MAINTAIN_SERVICE)

  const [
    createObject,
    { loading: createLoading, error: createError }
  ] = useMutation(CREATE_MAINTAIN_SERVICE)

  const { data: companyData, loading } = useQuery(GET_COMPANIES, {
    fetchPolicy: "cache-and-network"
  })

  const [selectedCompany, setSelectedCompany] = useState(
    (localData?.company && {
      value: localData?.company?.id || undefined,
      label: localData?.company?.name || undefined
    }) ||
      undefined
  )

  const [selectedUser, setSelectedUser] = useState(
    (localData?.user && {
      value: localData?.user?.id || undefined,
      label: localData?.user?.name || undefined
    }) ||
      undefined
  )

  const [selectedEngineer, setSelectedEngineer] = useState(
    (localData?.engineer && {
      value: localData?.engineer?.id || undefined,
      label: localData?.engineer?.name || undefined
    }) ||
      undefined
  )

  const [selectedCompanyDevice, setSelectedCompanyDevice] = useState(
    getCombinedOption(localData?.companyDevice, ["name", "number"], "device")
  )

  const getEngineerOptions = () => {
    return selectedCompany.value
      ? companyData?.companies?.data
          .find(company => company.area === "ALL")
          .users.filter(user => user.role === "ENGINEER")
          .map(user => {
            return {
              value: user.id,
              label: `${user.name}`
            }
          })
      : []
  }

  const onFieldChange = (key, value) => {
    setLocalData({
      ...localData,
      [key]: value
    })
  }

  const checkRequire = () => {
    return (
      localData.description === "" ||
      selectedCompany?.value === undefined ||
      selectedUser?.value === undefined ||
      selectedCompanyDevice?.value === undefined
    )
  }

  const variableFromData = () => {
    return {
      id: localData?.id || undefined,
      input: {
        description: localData.description,
        user: selectedUser.value,
        response: localData.id ? localData.response : undefined,
        finishTime: localData.finishTime ? localData.finishTime : undefined,
        companyDevice: selectedCompanyDevice?.value || undefined,
        engineer: selectedEngineer?.value || undefined,
        photoUrls: localData.id ? localData.photoUrls : undefined,
        files: localData.id ? localData.files : undefined
      }
    }
  }

  const onSaveButtonClick = async () => {
    if (localData.id) {
      await updateObject({
        variables: variableFromData()
      })
    } else {
      await createObject({
        variables: variableFromData()
      })
    }

    props.history.goBack()
  }

  return (
    <div className="">
      <Card>
        <CardHeader>
          <BackButton left />
        </CardHeader>

        <CardBody>
          {loading ? (
            <Spinner />
          ) : (
            <Form
              style={{
                marginLeft: "10px",
                marginRight: "10px"
              }}
            >
              <FormGroup row>
                <Label for="description">描述 *</Label>
                <Input
                  type="textarea"
                  name="description"
                  id="description"
                  placeholder="描述"
                  value={localData.description || ""}
                  onChange={e => {
                    onFieldChange("description", e.target.value)
                  }}
                />
              </FormGroup>
              <FormGroup row>
                <Label>選擇公司 *</Label>
                <div style={{ width: "100%" }}>
                  <Select
                    placeholder="選擇公司"
                    value={selectedCompany}
                    options={getOptions(companyData?.companies?.data, ["name"])}
                    onChange={option => {
                      setSelectedCompany(option)
                      setSelectedUser("")
                      setSelectedCompanyDevice("")
                    }}
                  />
                </div>
              </FormGroup>
              <FormGroup row>
                <Label>選擇客戶 *</Label>
                <div style={{ width: "100%" }}>
                  <Select
                    placeholder="選擇客戶"
                    value={selectedUser}
                    options={getAssociatedOptions(
                      companyData?.companies.data,
                      selectedCompany,
                      "users",
                      ["name"]
                    )}
                    onChange={option => setSelectedUser(option)}
                  />
                </div>
              </FormGroup>
              <FormGroup row>
                <Label>選擇公司設備 *</Label>
                <div style={{ width: "100%" }}>
                  <Select
                    placeholder="選擇公司設備"
                    value={selectedCompanyDevice}
                    options={getAssociatedOptions(
                      companyData?.companies?.data,
                      selectedCompany,
                      "companyDevices",
                      ["name", "number"],
                      "device"
                    )}
                    onChange={option => setSelectedCompanyDevice(option)}
                  />
                </div>
              </FormGroup>
              {localData.id ? (
                <>
                  <FormGroup
                    row
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <Label for="status">狀態</Label>
                    <Badge pill color="info" className="ml-2 mb-2">
                      {findTypeValueByKey(
                        maintainServiceStatusTypes,
                        localData?.status
                      )}
                    </Badge>
                  </FormGroup>

                  <FormGroup row>
                    <Label>選擇工程師</Label>
                    <div style={{ width: "100%" }}>
                      <Select
                        placeholder="選擇工程師"
                        value={selectedEngineer}
                        options={getEngineerOptions()}
                        onChange={option => setSelectedEngineer(option)}
                      />
                    </div>
                  </FormGroup>
                  {localData?.status !== "PROCESS" && (
                    <>
                      <FormGroup row>
                        <Label>維修完成時間</Label>
                        <div style={{ width: "100%" }}>
                          <Datetime
                            dateFormat="YYYY-MM-DD"
                            timeFormat="a h:mm"
                            closeOnSelect={true}
                            closeOnTab={true}
                            value={
                              localData.finishTime
                                ? moment(localData.finishTime).format(
                                    "YYYY-MM-DD a h:mm"
                                  )
                                : ""
                            }
                            onChange={date => {
                              onFieldChange(
                                "finishTime",
                                date.format("YYYY-MM-DD HH:mm:ss")
                              )
                            }}
                            inputProps={{
                              placeholder: "維修完成時間",
                              readOnly: true
                            }}
                          />
                        </div>
                      </FormGroup>
                    </>
                  )}
                  <FormGroup row>
                    <Label for="response">狀況回覆</Label>
                    <Input
                      type="textarea"
                      name="response"
                      id="response"
                      placeholder="狀況回覆"
                      value={localData.response || ""}
                      onChange={e => {
                        onFieldChange("response", e.target.value)
                      }}
                    />
                  </FormGroup>
                  <FormGroup row style={{ marginBottom: "0" }}>
                    <Label>圖片</Label>
                  </FormGroup>
                  <FormGroup row>
                    {localData.photoUrls.map((photo, index) => (
                      <Col xs={12} sm={6} md={4} xl={3} key={index}>
                        <Card
                          style={{
                            height: "320px",
                            overflowY: "hidden",
                            overflowX: "hidden",
                            position: "relative"
                          }}
                        >
                          <Img
                            src={`http://${photo}`}
                            name="維修圖片"
                            style={{
                              maxHeight: "320px"
                            }}
                          />
                          <div
                            style={{
                              position: "absolute",
                              left: "5%",
                              bottom: "5%"
                            }}
                          >
                            <Button
                              onClick={() => {
                                onFieldChange(
                                  "photoUrls",
                                  localData.photoUrls.filter(
                                    item => item !== photo
                                  )
                                )
                              }}
                              style={props.style}
                              size="sm"
                              round="true"
                              icon="true"
                              color="secondary"
                            >
                              <i className="fa fa-trash" />
                            </Button>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </FormGroup>
                  <FilePond
                    labelIdle='將檔案拖拉至此 或 <span class="filepond--label-action">瀏覽檔案</span>'
                    allowMultiple={true}
                    beforeAddFile={item => {
                      return new Promise(resolve => {
                        if (
                          item.file.type === "image/jpg" ||
                          item.file.type === "image/jpeg" ||
                          item.file.type === "image/png"
                        ) {
                          localData.files
                            ? setLocalData({
                                ...localData,
                                files: [...localData.files, item.file]
                              })
                            : setLocalData({
                                ...localData,
                                files: [item.file]
                              })

                          return resolve(true)
                        }
                        props.notify.warn("請上傳 jpeg, jpg, png 格式")
                        return resolve(false)
                      })
                    }}
                  />
                </>
              ) : (
                <Alert color="warning">
                  [提示] 編輯叫修紀錄的時候才可以上傳圖片跟指派工程師
                </Alert>
              )}
            </Form>
          )}
        </CardBody>
        <CardFooter>
          <div>
            <Button
              color="primary"
              disabled={checkRequire()}
              onClick={onSaveButtonClick}
            >
              送出
            </Button>
          </div>
        </CardFooter>
        <BarLoader loading={updateLoading || createLoading} />
      </Card>
      {(createError || updateError) && (
        <Alert color="danger">
          錯誤訊息：{`${createError || updateError}`}
        </Alert>
      )}
    </div>
  )
}

export default compose(
  withBasicNotify,
  withRouter
)(MaintainServiceForm)
